// public_events.js

import Http from "./http/utils.js";
import { icon } from './shared/icons/render.js';

const __ = window.__ || (s => s);

(() => {
  // ---------- Mini-HTTP: GET für Reads (kein CSRF), Session-Cookies an ---------
  const API_BASE = '/api/method/';
  const dev = !!window.dev_server;

  // ---------- Security: Escape für HTML-Attribute (XSS-Prävention) ----------
  function escapeAttr(str) {
    if (str == null) return '';
    return String(str).replace(/['"<>&]/g, c => ({
      "'": '&#39;',
      '"': '&quot;',
      '<': '&lt;',
      '>': '&gt;',
      '&': '&amp;'
    }[c]));
  }

  // ---------- Security: Escape für HTML-Content (XSS-Prävention) ----------
  function escapeHtml(str) {
    if (str == null) return '';
    return String(str).replace(/[&<>"']/g, c => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;'
    }[c]));
  }

  // ---------- Security: URL-Validierung für CSS url() Kontext ----------
  function safeCssUrl(url) {
    if (!url) return '';

    const raw = String(url).trim();
    if (!raw) return '';

    // allow absolute http(s) or site-local paths, but reject protocol-relative URLs.
    if (!/^(\/(?!\/)|https?:\/\/)/i.test(raw)) return '';

    // Block control chars and encode CSS-breaking chars for url('...') context.
    if (/[\u0000-\u001F\u007F]/.test(raw)) return '';

    return encodeURI(raw).replace(/['"()\\]/g, (ch) =>
      '%' + ch.charCodeAt(0).toString(16).toUpperCase()
    );
  }

  // ---------- Namespace für globale Funktionen ----------
  window.CitaStart = window.CitaStart || {};

  // ---------- Events Discover Filter State ----------
  const discoverFilters = {
    location: null,
    event_type: null,
    category: null,
    search: null
  };
  let discoverFilterOptions = null;
  let eventsSwiper = null;
  let watchlistSwiper = null; // Global für dynamische Updates
  let watchedEventNames = new Set(); // Global für Filter-Reload
  const pendingWatchToggles = new Set(); // Lock gegen Doppelklicks
  let _discoverLoadId = 0; // K-2 FIX: Race condition guard for filter-triggered reloads
  let _scrollOnNextLoad = false; // True when loadEventsOverview() was triggered by user filter/search interaction

  // ---------- Global Toggle Watch Function (ohne Page-Reload) ----------
  window.CitaStart.toggleWatch = async function(eventName, watch = null) {
    // Guard: Guests cannot use watchlist — prevent unnecessary API call
    if (Http.isGuestUser()) return;

    // Guard: inline_handlers.js passes (arg, el, event) — reject non-boolean watch
    if (watch !== null && typeof watch !== 'boolean') watch = null;

    // Lock prüfen - verhindert Doppelklicks während API läuft
    if (pendingWatchToggles.has(eventName)) return;

    // Wenn watch nicht angegeben, aktuellen Status aus watchedEventNames invertieren
    if (watch === null) {
      watch = !watchedEventNames.has(eventName);
    }

    // Lock setzen
    pendingWatchToggles.add(eventName);

    // Alle Buttons für dieses Event finden (Discover + Watchlist)
    const buttons = document.querySelectorAll(`[data-watch-event="${eventName}"]`);

    // Optimistic UI Update mit Animation
    buttons.forEach(btn => {
      btn.classList.toggle('active', watch);
      btn.classList.add('pulse');
      const svg = btn.querySelector('svg');
      if (svg) {
        svg.setAttribute('fill', watch ? 'currentColor' : 'none');
        if (!watch) svg.setAttribute('stroke', 'currentColor');
      }
      setTimeout(() => btn.classList.remove('pulse'), 300);
    });

    try {
      const { normalized, payload } = await Http.callMethod('cita_events.api.event.event_public_api.watch_event', {
        event: eventName,
        watch: watch
      });

      if (normalized.success) {
        // watchedEventNames aktualisieren
        if (watch) {
          watchedEventNames.add(eventName);
        } else {
          watchedEventNames.delete(eventName);
        }

        // Watchlist-Carousel aktualisieren
        updateWatchlistUI(eventName, watch);
      } else {
        // Bei Fehler: UI zurücksetzen
        revertWatchButtons(buttons, !watch);
        console.error('Watch toggle failed', payload);
      }
    } catch (err) {
      // Bei Fehler: UI zurücksetzen
      revertWatchButtons(buttons, !watch);
      console.error('Watch toggle error', err);
    } finally {
      // Lock entfernen
      pendingWatchToggles.delete(eventName);
    }
  };

  // ---------- Watchlist UI Update Helpers ----------
  function updateWatchlistUI(eventName, added) {
    const watchlistWrapper = document.getElementById('watchlist-swiper-wrapper');
    const watchlistEmpty = document.getElementById('watchlist-empty');
    const watchlistCount = document.getElementById('watchlist-count');
    const watchlistSection = document.querySelector('.watchlist-carousel')?.closest('section');
    const swiperContainer = watchlistSection?.querySelector('.swiper');

    if (added) {
      // DUPLIKAT-PRÜFUNG: Event bereits in Watchlist?
      const existingSlide = watchlistWrapper?.querySelector(`[data-watch-event="${eventName}"]`);
      if (existingSlide) return; // Bereits vorhanden, nichts tun

      // Event zur Watchlist hinzufügen - Daten aus Discover-Card holen
      const discoverCard = document.querySelector(`.discover-card [data-watch-event="${eventName}"]`)?.closest('.discover-card');
      if (discoverCard && watchlistWrapper) {
        const newSlide = createWatchlistSlideFromDiscover(discoverCard, eventName);
        watchlistWrapper.appendChild(newSlide);
        watchlistEmpty?.classList.add('hidden');
        swiperContainer?.classList.remove('hidden');
        // Swiper aktualisieren
        watchlistSwiper?.update();
      }
    } else {
      // Event aus Watchlist entfernen
      const watchlistSlide = watchlistWrapper?.querySelector(`[data-watch-event="${eventName}"]`)?.closest('.swiper-slide');
      if (watchlistSlide) {
        watchlistSlide.style.transition = 'all 0.2s ease';
        watchlistSlide.style.opacity = '0';
        watchlistSlide.style.transform = 'scale(0.8)';
        setTimeout(() => {
          watchlistSlide.remove();
          watchlistSwiper?.update();
          // Empty State prüfen
          const remainingSlides = watchlistWrapper?.querySelectorAll('.swiper-slide').length || 0;
          if (remainingSlides === 0) {
            watchlistEmpty?.classList.remove('hidden');
            swiperContainer?.classList.add('hidden');
          }
        }, 200);
      }
    }

    // Count aktualisieren
    if (watchlistCount) {
      // Kurz warten falls Slide entfernt wird
      setTimeout(() => {
        const count = watchlistWrapper?.querySelectorAll('.swiper-slide').length || 0;
        watchlistCount.textContent = count;
      }, 250);
    }
  }

  // Watchlist-Slide aus Discover-Card Daten erstellen
  function createWatchlistSlideFromDiscover(discoverCard, eventName) {
    const slide = document.createElement('div');
    slide.className = 'swiper-slide';
    slide.style.opacity = '0';
    slide.style.transform = 'scale(0.8)';

    const title = discoverCard.querySelector('.discover-title')?.textContent || 'Event';
    const img = discoverCard.querySelector('.discover-header img')?.src || '/assets/cita_events/images/event/cita_default_messe.png';
    const dateEl = discoverCard.querySelector('.discover-date');
    const slugMatch = discoverCard.querySelector('.discover-btn-details')?.href?.match(/\/event\/([^#?]+)/);
    const slug = slugMatch ? slugMatch[1] : eventName;

    // Datum extrahieren
    const day = dateEl?.querySelector('.discover-date-day')?.textContent || '';
    const month = dateEl?.querySelector('.discover-date-month')?.textContent || '';

    slide.innerHTML = `
      <div class="watchlist-card">
        <div class="watchlist-header">
          <img src="${escapeAttr(img)}" alt="${escapeAttr(title)}" loading="lazy" />
          <div class="watchlist-header-gradient"></div>
          ${day ? `<div class="watchlist-date"><span class="watchlist-date-day">${escapeHtml(day)}</span><span class="watchlist-date-month">${escapeHtml(month)}</span></div>` : ''}
          <button class="watchlist-heart active" data-watch-event="${escapeAttr(eventName)}"
                  data-cita-action="CitaStart.toggleWatch" data-cita-arg="${escapeAttr(eventName)}" data-cita-arg-type="string" data-cita-prevent="true" data-cita-stop="true"
                  title="${__('Von Watchlist entfernen')}">
            ${icon('heart')}
          </button>
        </div>
        <div class="watchlist-content">
          <h3 class="watchlist-title">${escapeHtml(title)}</h3>
          <div class="watchlist-actions">
            <a href="/event/${escapeAttr(slug)}" class="watchlist-btn">Details</a>
          </div>
        </div>
      </div>
    `;

    // Einblend-Animation
    requestAnimationFrame(() => {
      slide.style.transition = 'all 0.3s ease';
      slide.style.opacity = '1';
      slide.style.transform = 'scale(1)';
    });

    return slide;
  }

  // Button-State bei Fehler zurücksetzen
  function revertWatchButtons(buttons, wasWatched) {
    buttons.forEach(btn => {
      btn.classList.toggle('active', wasWatched);
      btn.classList.remove('pulse');
      const svg = btn.querySelector('svg');
      if (svg) svg.setAttribute('fill', wasWatched ? 'currentColor' : 'none');
    });
  }

  function qs(obj = {}) {
    const entries = Object.entries(obj).filter(([, v]) => v !== undefined && v !== null && String(v) !== '');
    return new URLSearchParams(entries).toString();
  }

  async function apiGet(method, params = {}, { signal } = {}) {
    const query = qs(params);
    const url = `${API_BASE}${method}${query ? `?${query}` : ''}`;
    const { res, payload, normalized } = await Http.fetchJson(url, {
      method: 'GET',
      cache: 'no-store',
      signal
    });
    return { res, payload, normalized };
  }

  // ---------- Utils ----------
  const $ = (sel) => document.querySelector(sel);

  function formatDate(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return String(dateStr);
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  function formatDateBadge(dateStr) {
    if (!dateStr) return { day: '', month: '' };
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return { day: '', month: '' };
    const day = d.getDate();
    const month = d.toLocaleDateString('de-DE', { month: 'short' }).replace('.', '');
    return { day, month };
  }

  function daysUntil(dateStr) {
    const today = new Date();
    const target = new Date(dateStr);
    const diff = Math.ceil((target - today) / (1000 * 60 * 60 * 24));
    return diff;
  }

  // ---------- Location/Meeting Info based on event_mode ----------
  // Returns HTML for location display based on event_mode:
  // - vor_ort: Shows physical location only
  // - online: Shows meeting info only (platform name)
  // - hybrid: Shows both physical location AND meeting info
  function renderLocationInfo(event, fallback = __('Ort folgt')) {
    const mode = event.event_mode || 'vor_ort';
    const parts = [];

    // Physical location (vor_ort + hybrid)
    if ((mode === 'vor_ort' || mode === 'hybrid') && event.location) {
      parts.push(escapeHtml(event.location));
    }

    // Online meeting info (online + hybrid)
    if ((mode === 'online' || mode === 'hybrid') && (event.online_link || event.online_platform)) {
      const platform = event.online_platform || 'Online';
      parts.push(escapeHtml(platform));
    }

    return parts.length > 0 ? parts.join(' / ') : fallback;
  }

  // Returns location icon SVG based on event_mode
  function getLocationIcon(event) {
    const mode = event.event_mode || 'vor_ort';

    if (mode === 'online') {
      // Video/Online icon
      return `${icon('video')}`;
    } else if (mode === 'hybrid') {
      // Combined icon (location + video)
      return `${icon('mapPin')}`;
    }
    // Default: location pin icon (vor_ort)
    return `${icon('mapPin')}`;
  }

  // ---------- Status Badge für Registrierungen ----------
  function getStatusBadge(reg) {
    // Priorität: Eingecheckt > Warteliste > Angebot > Ausstehend > Zahlung offen > Gekauft
    // Verwendet jetzt einfache CSS-Klassen wie my-events (.confirmed, .waitlist, etc.)
    if (reg.checked_in) {
      return { text: __('Eingecheckt'), cssClass: 'checkedin', icon: 'M5 13l4 4L19 7' };
    }
    if (reg.registration_status === 'Warteliste') {
      return { text: __('Warteliste'), cssClass: 'waitlist', icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' };
    }
    if (reg.waitlist_offer_status === 'offered') {
      return { text: __('Angebot!'), cssClass: 'offer', icon: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9' };
    }
    if (reg.approval_status === 'pending') {
      return { text: __('Ausstehend'), cssClass: 'pending', icon: 'M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z' };
    }
    if (!reg.has_paid && reg.registration_status !== 'Registriert' && reg.registration_status !== 'Warteliste') {
      return { text: __('Zahlung offen'), cssClass: 'payment-pending', icon: 'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z' };
    }
    return { text: __('Gekauft'), cssClass: 'confirmed', icon: 'M5 13l4 4L19 7' };
  }

  // ---------- Event Countdown Badge ----------
  function getEventCountdown(startDate) {
    if (!startDate) return null;
    const now = new Date();
    const eventStart = new Date(startDate);
    const diffMs = eventStart - now;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    // Event läuft gerade (innerhalb der letzten 24h gestartet)
    if (diffMs < 0 && diffMs > -86400000) {
      return { text: __('Läuft jetzt!'), cssClass: 'urgent' };
    }
    if (diffHours <= 0) return null; // Vorbei
    if (diffHours <= 24) {
      return { text: __('In {0}h').replace('{0}', diffHours), cssClass: 'urgent' };
    }
    if (diffDays <= 1) {
      return { text: __('Morgen'), cssClass: 'soon' };
    }
    if (diffDays <= 7) {
      return { text: __('In {0} Tagen').replace('{0}', diffDays), cssClass: '' };
    }
    return null;
  }

  // ---------- Event Urgency Badge (für Watchlist/Event Cards) ----------
  function getEventUrgency(startDate) {
    if (!startDate) return null;
    const now = new Date();
    const eventStart = new Date(startDate);
    const diffMs = eventStart - now;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    // Event läuft gerade
    if (diffMs < 0 && diffMs > -86400000) {
      return { text: __('Läuft jetzt!'), bg: 'bg-red-500/20', border: 'border-red-500/50', color: 'text-red-400' };
    }
    if (diffHours <= 0) return null; // Vorbei
    if (diffHours <= 24) {
      return { text: __('In {0}h').replace('{0}', diffHours), bg: 'bg-red-500/20', border: 'border-red-500/50', color: 'text-red-400' };
    }
    if (diffDays <= 1) {
      return { text: __('Morgen'), bg: 'bg-amber-500/20', border: 'border-amber-500/50', color: 'text-amber-400' };
    }
    if (diffDays <= 7) {
      return { text: __('In {0} Tagen').replace('{0}', diffDays), bg: 'bg-cyan-500/20', border: 'border-cyan-500/50', color: 'text-cyan-400' };
    }
    return null;
  }

  // ---------- Apple Wallet Button HTML ----------
  function getWalletButton(passUrl) {
    if (!passUrl) return '';
    return `
      <a href="${escapeAttr(passUrl)}"
         class="absolute bottom-3 right-3 z-20 bg-black/80 backdrop-blur-sm rounded-lg px-2.5 py-1.5 flex items-center gap-1.5 text-xs text-white hover:bg-black transition shadow-lg"
         target="_blank"
         rel="noopener noreferrer"
         title="${__('Zu Apple Wallet hinzufügen')}">
        <svg class="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
          <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
        </svg>
        Wallet
      </a>`;
  }

  // ---------- Kapazitäts-Badge (Nur noch X Plätze) ----------
  function getCapacityBadge(event) {
    // Nur anzeigen wenn vom Organizer aktiviert UND Plätze verfügbar
    if (!event.ui_remaining_allowed) return '';
    const available = event.available_tickets;
    const total = event.total_tickets;
    if (typeof available !== 'number' || typeof total !== 'number') return '';

    // Ausverkauft
    if (available <= 0) {
      return `
        <div class="absolute top-3 right-3 z-20 badge-sold-out backdrop-blur-sm rounded-full px-2.5 py-1 text-xs font-medium flex items-center gap-1 shadow-lg">
          ${icon('noSymbol', { size: 14, className: 'w-3.5 h-3.5' })}
          ${__('Ausverkauft')}
        </div>`;
    }

    // Nur noch wenige Plätze (< 10 oder < 20% der Kapazität)
    const isLow = available <= 10 || (total > 0 && (available / total) < 0.2);
    if (isLow) {
      return `
        <div class="absolute top-3 right-3 z-20 badge-low-stock backdrop-blur-sm rounded-full px-2.5 py-1 text-xs font-medium flex items-center gap-1 shadow-lg animate-pulse">
          ${icon('exclamation', { size: 14, className: 'w-3.5 h-3.5' })}
          ${__('Nur noch {0} {1}!').replace('{0}', available).replace('{1}', available === 1 ? __('Platz') : __('Plätze'))}
        </div>`;
    }

    // Normale Anzeige (optional, nur wenn gewünscht)
    // return `<div class="absolute top-3 right-3 z-20 surface-elevated/80 rounded-full px-2 py-0.5 text-xs text-secondary">${available} verfügbar</div>`;
    return '';
  }

  // ---------- Highlight-Badge (Stern für hervorgehobene Events) ----------
  function getHighlightBadge(event) {
    if (!event.is_highlighted) return '';
    return `
      <div class="absolute top-12 left-3 z-20 badge-highlight
                  rounded-full px-2.5 py-1 text-xs font-bold flex items-center gap-1 shadow-lg">
        ${icon('starFilled', { size: 14, className: 'w-3.5 h-3.5' })}
        Highlight
      </div>`;
  }

  // ---------- Watch-Button Helper ----------
  // Position: top-12 right-3 (unter dem Kapazitäts-Badge)
  // Verwendet escapeAttr() für XSS-Prävention
  function getWatchButton(eventName, isWatched) {
    const safeEventName = escapeAttr(eventName);
    if (isWatched) {
      // Gefülltes Herz = auf Watchlist
      return `
        <button class="absolute top-12 right-3 z-20 badge-watched border rounded-full p-1.5 hover:opacity-80 transition active"
                data-watch-event="${safeEventName}"
                data-cita-action="CitaStart.toggleWatch" data-cita-arg="${safeEventName}" data-cita-arg-type="string" data-cita-prevent="true" data-cita-stop="true"
                title="${__('Von Watchlist entfernen')}">
          ${icon('heart', { size: 14, className: 'w-3.5 h-3.5' })}
        </button>`;
    } else {
      // Leeres Herz = nicht auf Watchlist
      return `
        <button class="absolute top-12 right-3 z-20 surface-elevated border border-strong-theme rounded-full p-1.5 text-secondary hover:text-red-400 hover:bg-red-500/20 hover:border-red-500/50 transition"
                data-watch-event="${safeEventName}"
                data-cita-action="CitaStart.toggleWatch" data-cita-arg="${safeEventName}" data-cita-arg-type="string" data-cita-prevent="true" data-cita-stop="true"
                title="${__('Zur Watchlist hinzufügen')}">
          ${icon('heart', { size: 14, className: 'w-3.5 h-3.5' })}
        </button>`;
    }
  }

  // ---------- Events Discover Filter Functions ----------

  // Load filter options from API
  async function loadDiscoverFilterOptions() {
    try {
      const { normalized } = await apiGet("cita_events.api.event.event_public_api.get_filter_options");
      if (normalized.success) {
        discoverFilterOptions = normalized.data || {};
        renderFilterDropdown('location', discoverFilterOptions.locations || []);
        renderFilterDropdown('event_type', discoverFilterOptions.event_types || []);
        renderFilterDropdown('category', discoverFilterOptions.categories || []);
      }
    } catch (err) {
      if (dev) console.warn('Filter options fetch failed', err);
    }
  }

  // Render dropdown options
  function renderFilterDropdown(filterKey, options) {
    const dropdown = document.querySelector(`[data-filter-dropdown="${filterKey}"]`);
    if (!dropdown || !options.length) return;

    const currentValue = discoverFilters[filterKey];
    dropdown.innerHTML = options.map(opt => `
      <div class="events-discover-dropdown-item${opt.value === currentValue ? ' selected' : ''}" data-value="${escapeAttr(opt.value)}" data-filter="${filterKey}">
        <span>${escapeHtml(opt.label)}</span>
        <span class="events-discover-dropdown-count">${opt.count}</span>
      </div>
    `).join('');
  }

  // ---------- URL-State Persistence (shareable deep-links + browser back/forward) ----------
  const URL_FILTER_KEYS = ['location', 'event_type', 'category', 'search'];

  function applyFiltersFromURL() {
    const urlParams = new URLSearchParams(window.location.search);
    let anyApplied = false;
    for (const key of URL_FILTER_KEYS) {
      const val = urlParams.get(key);
      if (val) {
        discoverFilters[key] = val;
        anyApplied = true;
      }
    }
    if (!anyApplied) return;

    // Deep-link with filters: scroll results into view on first load
    _scrollOnNextLoad = true;

    // Reflect search value in the input
    if (discoverFilters.search) {
      const searchInput = document.getElementById('guest-search');
      if (searchInput) searchInput.value = discoverFilters.search;
    }

    // Reflect dropdown-filter values in the filter buttons
    const defaultLabels = { location: __('Ort'), event_type: __('Typ'), category: __('Kategorie') };
    Object.keys(defaultLabels).forEach(key => {
      const value = discoverFilters[key];
      const label = document.querySelector(`[data-filter-label="${key}"]`);
      const button = document.querySelector(`[data-filter-toggle="${key}"]`);
      if (label) label.textContent = value || defaultLabels[key];
      if (button) {
        button.classList.toggle('has-selection', !!value);
        const countBadge = button.querySelector(`[data-filter-count="${key}"]`);
        if (countBadge) countBadge.textContent = value ? '1' : '';
      }
    });
    updateResetButton();
  }

  function syncFiltersToURL() {
    try {
      const url = new URL(window.location.href);
      for (const key of URL_FILTER_KEYS) {
        const val = discoverFilters[key];
        if (val) {
          url.searchParams.set(key, val);
        } else {
          url.searchParams.delete(key);
        }
      }
      // replaceState (not pushState) — rapid filter changes shouldn't flood history
      window.history.replaceState({}, '', url.toString());
    } catch (err) {
      if (dev) console.warn('URL sync failed', err);
    }
  }

  // Toggle dropdown visibility
  function toggleFilterDropdown(filterKey, forceClose = false) {
    const dropdown = document.querySelector(`[data-filter-dropdown="${filterKey}"]`);
    const button = document.querySelector(`[data-filter-toggle="${filterKey}"]`);
    if (!dropdown) return;

    const isOpen = !dropdown.classList.contains('hidden');

    // Close all dropdowns first
    document.querySelectorAll('.events-discover-dropdown').forEach(d => d.classList.add('hidden'));
    document.querySelectorAll('.events-discover-tile').forEach(b => b.classList.remove('active'));

    if (!isOpen && !forceClose) {
      dropdown.classList.remove('hidden');
      button?.classList.add('active');
    }
  }

  // Set filter value and reload events
  function setDiscoverFilter(filterKey, value) {
    discoverFilters[filterKey] = value;

    // Update button label
    const label = document.querySelector(`[data-filter-label="${filterKey}"]`);
    const defaultLabels = { location: __('Ort'), event_type: __('Typ'), category: __('Kategorie') };
    if (label) {
      label.textContent = value || defaultLabels[filterKey];
    }

    // Update button active state (has-selection shows cyan highlight)
    const button = document.querySelector(`[data-filter-toggle="${filterKey}"]`);
    if (button) {
      button.classList.toggle('has-selection', !!value);
      // Update count badge
      const countBadge = button.querySelector(`[data-filter-count="${filterKey}"]`);
      if (countBadge) {
        countBadge.textContent = value ? '1' : '';
      }
    }

    // Update dropdown item selection
    const dropdown = document.querySelector(`[data-filter-dropdown="${filterKey}"]`);
    if (dropdown) {
      dropdown.querySelectorAll('.events-discover-dropdown-item').forEach(item => {
        item.classList.toggle('selected', item.dataset.value === value);
      });
    }

    // Show/hide reset button
    updateResetButton();

    // Close dropdown
    toggleFilterDropdown(filterKey, true);

    // Persist filter state in URL for share-ability + browser history
    syncFiltersToURL();

    // Reload events with new filters — user-initiated, so scroll results into view
    _scrollOnNextLoad = true;
    loadEventsOverview();
  }

  // Update reset button visibility and count
  function updateResetButton() {
    const resetBtn = document.getElementById('events-discover-reset');
    const countEl = document.getElementById('events-discover-count');
    const activeCount = Object.values(discoverFilters).filter(v => v !== null).length;

    if (resetBtn) {
      resetBtn.classList.toggle('hidden', activeCount === 0);
    }
    if (countEl) {
      countEl.textContent = activeCount;
    }

    // Update active filter tags
    renderActiveTags();
  }

  // Render active filter tags
  function renderActiveTags() {
    const container = document.getElementById('events-discover-tags');
    if (!container) return;

    const labelMap = { location: __('Ort'), event_type: __('Event Typ'), category: __('Event Art'), search: __('Suche') };
    const activeFilters = Object.entries(discoverFilters).filter(([k, v]) => v !== null);

    if (activeFilters.length === 0) {
      container.classList.add('hidden');
      container.innerHTML = '';
      return;
    }

    container.classList.remove('hidden');
    container.innerHTML = activeFilters.map(([key, value]) => `
      <span class="events-discover-tag">
        <span class="events-discover-tag-label">${escapeHtml(labelMap[key])}:</span>
        ${escapeHtml(value)}
        <span class="events-discover-tag-remove" data-remove-filter="${key}">
          ${icon('xMark')}
        </span>
      </span>
    `).join('');
  }

  // Reset all filters
  function resetDiscoverFilters() {
    discoverFilters.location = null;
    discoverFilters.event_type = null;
    discoverFilters.category = null;
    discoverFilters.search = null;

    // Clear search input
    const searchInput = document.getElementById('guest-search');
    if (searchInput) searchInput.value = '';

    // Reset UI
    const defaultLabels = { location: __('Ort'), event_type: __('Typ'), category: __('Kategorie') };
    Object.keys(defaultLabels).forEach(key => {
      const label = document.querySelector(`[data-filter-label="${key}"]`);
      const button = document.querySelector(`[data-filter-toggle="${key}"]`);
      const dropdown = document.querySelector(`[data-filter-dropdown="${key}"]`);
      if (label) label.textContent = defaultLabels[key];
      if (button) button.classList.remove('active');
      if (dropdown) dropdown.querySelectorAll('.events-discover-dropdown-item').forEach(item => item.classList.remove('selected'));
    });

    updateResetButton();
    // Clear URL filter params on reset
    syncFiltersToURL();
    // User clicked reset — keep them oriented by scrolling the updated list into view
    _scrollOnNextLoad = true;
    loadEventsOverview();
  }

  // Setup filter event listeners
  function setupDiscoverFilters() {
    // Toggle dropdown on button click
    document.querySelectorAll('[data-filter-toggle]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const filterKey = btn.dataset.filterToggle;
        toggleFilterDropdown(filterKey);
      });
    });

    // Select filter option
    document.addEventListener('click', (e) => {
      const item = e.target.closest('.events-discover-dropdown-item');
      if (item) {
        e.stopPropagation();
        const filterKey = item.dataset.filter;
        const value = item.dataset.value;
        // Toggle: click again to deselect
        const currentValue = discoverFilters[filterKey];
        setDiscoverFilter(filterKey, currentValue === value ? null : value);
      }
    });

    // Close dropdowns on outside click
    document.addEventListener('click', (e) => {
      if (!e.target.closest('[data-filter-group]')) {
        document.querySelectorAll('.events-discover-dropdown').forEach(d => d.classList.add('hidden'));
        document.querySelectorAll('[data-filter-toggle]').forEach(b => {
          const key = b.dataset.filterToggle;
          b.classList.toggle('active', !!discoverFilters[key]);
        });
      }
    });

    // Reset button
    const resetBtn = document.getElementById('events-discover-reset');
    if (resetBtn) {
      resetBtn.addEventListener('click', resetDiscoverFilters);
    }

    // Empty-state reset button (shown when filter/search returned no events)
    const emptyResetBtn = document.getElementById('all-events-empty-reset');
    if (emptyResetBtn) {
      emptyResetBtn.addEventListener('click', resetDiscoverFilters);
    }

    // Tag remove click (event delegation)
    document.addEventListener('click', (e) => {
      const removeBtn = e.target.closest('[data-remove-filter]');
      if (removeBtn) {
        e.stopPropagation();
        const filterKey = removeBtn.dataset.removeFilter;
        if (filterKey === 'search') {
          const searchInput = document.getElementById('guest-search');
          if (searchInput) searchInput.value = '';
        }
        setDiscoverFilter(filterKey, null);
      }
    });

    // Live search: debounced input on #guest-search filters the discover carousel
    const guestSearchInput = document.getElementById('guest-search');
    if (guestSearchInput) {
      let _searchDebounce = null;
      guestSearchInput.addEventListener('input', () => {
        clearTimeout(_searchDebounce);
        _searchDebounce = setTimeout(() => {
          const val = (guestSearchInput.value || '').trim() || null;
          setDiscoverFilter('search', val);
        }, 300);
      });
    }
  }

  // Load Events Overview with filters
  // K-2 FIX: Added race condition guard so only the latest filter request renders.
  async function loadEventsOverview() {
    const wrapper = document.getElementById('events-swiper-wrapper');
    if (!wrapper) return;

    const thisLoadId = ++_discoverLoadId;

    // Show loading state
    wrapper.innerHTML = '';
    for (let i = 0; i < 4; i++) {
      const sk = document.createElement('div');
      sk.className = 'swiper-slide';
      sk.innerHTML = `<div class="skeleton-card">
        <div class="skeleton-line"></div><div class="skeleton-small-line"></div>
        <div class="skeleton-line"></div><div class="skeleton-small-line"></div>
      </div>`;
      wrapper.appendChild(sk);
    }

    // Destroy existing swiper if exists
    if (eventsSwiper) {
      eventsSwiper.destroy(true, true);
      eventsSwiper = null;
    }

    try {
      // Build filter params
      const params = { limit: 10 };
      if (discoverFilters.location) params.location = discoverFilters.location;
      if (discoverFilters.event_type) params.event_type = discoverFilters.event_type;
      if (discoverFilters.category) params.category = discoverFilters.category;
      if (discoverFilters.search) params.search = discoverFilters.search;

      const { res, payload } = await apiGet("cita_events.api.event.event_public_api.get_events_overview", params);
      // K-2 FIX: Discard stale responses from superseded filter changes
      if (thisLoadId !== _discoverLoadId) return;
      wrapper.innerHTML = "";
      const items = payload?.data?.events || [];

      const emptyState = document.getElementById('all-events-empty');
      const carousel = document.querySelector('.all-events-carousel');

      if (payload?.success && Array.isArray(items) && items.length) {
        // Hide empty state, show carousel
        if (emptyState) emptyState.classList.add('hidden');
        if (carousel) carousel.classList.remove('hidden');

        for (const event of items) {
          const slide = document.createElement('div');
          slide.className = 'swiper-slide';

          const d = daysUntil(event.start_date);
          const dateBadge = formatDateBadge(event.start_date);
          const isWatched = watchedEventNames.has(event.name);
          // Use the proper highlight badge function
          const highlightBadge = getHighlightBadge(event);
          // "Bald Start!" badge for events starting within 7 days (in addition to highlight)
          const soonBadge = (!event.is_highlighted && d > 0 && d <= 7)
            ? `<div class="badge highlight glass-badge flex items-center gap-1 px-2 py-0.5">
                <svg class="w-4 h-4 text-orange-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path d="M12 2v2"/><path d="M12 20v2"/><path d="M4.93 4.93l1.41 1.41"/>
                  <path d="M17.66 17.66l1.41 1.41"/><path d="M2 12h2"/><path d="M20 12h2"/>
                  <path d="M4.93 19.07l1.41-1.41"/><path d="M17.66 6.34l1.41-1.41"/>
                  <circle cx="12" cy="12" r="5"/>
                </svg>
                ${__('Bald Start!')}
              </div>`
            : "";

          const imageUrl = event.banner_image || event.image || event.default_image || '/assets/cita_events/images/event/cita_default_messe.png';
          // Add highlight border class for highlighted events
          const highlightClass = event.is_highlighted ? 'ring-2 ring-cyan-500/50' : '';

          const slugAttr = escapeAttr(event.slug || '');
          const stretchedLink = event.slug
            ? `<a class="discover-card-link" href="/event/${slugAttr}" aria-label="${escapeAttr(event.title)} – ${escapeAttr(__('Details ansehen'))}">${escapeHtml(event.title)}</a>`
            : '';
          slide.innerHTML = `
            <article class="discover-card ${highlightClass}">
              ${stretchedLink}
              <div class="discover-header">
                <img src="${escapeAttr(imageUrl)}" alt="${escapeAttr(event.title)}" loading="lazy" />
                <div class="discover-header-gradient"></div>
                ${highlightBadge}
                ${soonBadge}
                ${dateBadge.day ? `
                <div class="discover-date">
                  <div class="discover-date-day">${dateBadge.day}</div>
                  <div class="discover-date-month">${dateBadge.month}</div>
                </div>` : ''}
                <button class="discover-watch ${isWatched ? 'active' : ''}"
                        data-watch-event="${escapeAttr(event.name)}"
                        data-cita-action="CitaStart.toggleWatch" data-cita-arg="${escapeAttr(event.name)}" data-cita-arg-type="string" data-cita-prevent="true" data-cita-stop="true"
                        title="${isWatched ? __('Von Merkliste entfernen') : __('Zur Merkliste hinzufügen')}">
                  ${icon('heart')}
                </button>
              </div>
              <div class="discover-content">
                ${event.event_type ? `<span class="discover-type">${escapeHtml(event.event_type)}</span>` : ''}
                <h3 class="discover-title">${escapeHtml(event.title)}</h3>
                <div class="discover-meta">
                  <div class="discover-meta-item">
                    ${getLocationIcon(event)}
                    <span>${renderLocationInfo(event, __('Ort folgt'))}</span>
                  </div>
                  <div class="discover-meta-item">
                    ${icon('calendar')}
                    <span>${formatDate(event.start_date)}${event.end_date ? ` – ${formatDate(event.end_date)}` : ''}</span>
                  </div>
                </div>
                <div class="discover-actions">
                  <a href="/event/${slugAttr}" class="discover-btn discover-btn-details">
                    ${icon('info')}
                    Details
                  </a>
                  ${event.tickets_available ? `
                  <a href="/event/${slugAttr}#tickets" class="discover-btn discover-btn-tickets">
                    <svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"/>
                    </svg>
                    Tickets
                  </a>
                  ` : ''}
                </div>
              </div>
            </article>
          `;
          wrapper.appendChild(slide);
        }

        // Initialize Swiper
        eventsSwiper = new Swiper('.all-events-carousel', {
          slidesPerView: 1.2,
          spaceBetween: 12,
          navigation: { nextEl: '.all-events-carousel .swiper-button-next', prevEl: '.all-events-carousel .swiper-button-prev' },
          pagination: {
            el: '.all-events-carousel .swiper-pagination',
            clickable: true,
            renderBullet: function (index, className) {
              return `<span class="discover-dot ${className}"></span>`;
            }
          },
          breakpoints: {
            480: { slidesPerView: 1.5, spaceBetween: 14 },
            640: { slidesPerView: 2.2, spaceBetween: 16 },
            1024: { slidesPerView: 2.8, spaceBetween: 20 }
          }
        });
      } else {
        // Empty State
        wrapper.innerHTML = '';
        if (emptyState) emptyState.classList.remove('hidden');
        if (carousel) carousel.classList.add('hidden');
        // Show reset button only when active filters/search caused the empty result
        const emptyResetBtn = document.getElementById('all-events-empty-reset');
        if (emptyResetBtn) {
          const hasActiveFilters = Object.values(discoverFilters).some(v => v !== null);
          emptyResetBtn.classList.toggle('hidden', !hasActiveFilters);
        }
      }

      // Auto-scroll to results when a filter/search was triggered by the user.
      // Skip on initial page load so the homepage renders normally.
      if (_scrollOnNextLoad) {
        _scrollOnNextLoad = false;
        const target = (emptyState && !emptyState.classList.contains('hidden'))
          ? emptyState
          : carousel;
        if (target) {
          const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
        }
      }
    } catch (err) {
      if (dev) console.error('Events Overview failed', err);
      wrapper.innerHTML = `<div class="text-center text-secondary py-10">${__('Fehler beim Laden der Events.')}</div>`;
      _scrollOnNextLoad = false;
    }
  }

  // ---------- DOM Ready ----------
  document.addEventListener("DOMContentLoaded", async () => {
    // --- Konsolidierter Dashboard-Endpoint (User + Registrations + Stats + Watchlist) ---
    // Wird ZUERST geladen, damit wir Watchlist-Status für Events haben
    // GUARD: Nur für authentifizierte User — Gäste haben keinen Zugriff
    const isGuest = Http.isGuestUser();
    let dashboardData = null;
    if (!isGuest) {
      try {
        const { normalized } = await apiGet("cita_events.api.dashboard.get_dashboard_data");
        if (normalized.success) {
          dashboardData = normalized.data || {};
          // Set der gemerkten Event-Namen erstellen (global für Filter-Reload)
          if (dashboardData.watchlist && Array.isArray(dashboardData.watchlist)) {
            dashboardData.watchlist.forEach(e => watchedEventNames.add(e.name));
          }
        }
      } catch (err) {
        if (dev) console.warn('Dashboard data fetch failed', err);
      }
    }

    // --- Events Overview (Startseite-Karussell) mit Filtern ---
    const wrapper = document.getElementById('events-swiper-wrapper');
    if (wrapper) {
      // Setup filter event listeners
      setupDiscoverFilters();
      // Hydrate filters from URL so share-able deep-links + browser back/forward work
      applyFiltersFromURL();
      // Load filter options (async, non-blocking) — reflects selected state via discoverFilters
      loadDiscoverFilterOptions();
      // Initial load of events — uses hydrated filter state
      await loadEventsOverview();
    }

    // --- User Context (Hero Section) ---
    // Nutzt die konsolidierten Daten vom Dashboard-Endpoint
    try {
      const nameEl = document.getElementById("user-name");
      const idEl   = document.getElementById("user-citaid");
      const avaEl  = document.getElementById("user-avatar");
      if ((nameEl || idEl || avaEl) && dashboardData?.user) {
        const user = dashboardData.user;
        if (nameEl && user.first_name) nameEl.textContent = user.first_name;
        if (idEl && user.cita_id) idEl.textContent = "Cita-ID: " + user.cita_id;
        if (avaEl && user.profile_pic) avaEl.src = user.profile_pic;
      }
    } catch (err) {
      if (dev) console.warn('User context rendering failed', err);
    }

    // --- Meine Events (Karussell) ---
    // Nutzt die konsolidierten Daten vom Dashboard-Endpoint
    const myEventsWrapper = document.getElementById('my-events-swiper-wrapper');
    if (myEventsWrapper) {
      for (let i = 0; i < 2; i++) {
        const sk = document.createElement('div');
        sk.className = 'swiper-slide';
        sk.innerHTML = `<div class="skeleton-card"><div class="skeleton-line"></div><div class="skeleton-small-line"></div></div>`;
        myEventsWrapper.appendChild(sk);
      }

      try {
        const regs = dashboardData?.registrations || [];
        myEventsWrapper.innerHTML = "";
        if (Array.isArray(regs) && regs.length) {
          for (const registration of regs) {
            const slide = document.createElement('div');
            slide.className = 'swiper-slide';
            const statusBadge = getStatusBadge(registration);
            const countdown = getEventCountdown(registration.start_date);
            const imageUrl = registration.banner_image || registration.image || registration.default_image || '/assets/cita_events/images/event/cita_default_messe.png';
            // Issue #1009: the tile body (image, title, meta) was not
            // clickable — only the explicit "Details"/"Ticket" buttons
            // worked, which made the rest of the card feel inert. Compute
            // the event href once and attach it to the card so a click
            // anywhere outside the inner action buttons opens the event.
            const eventSlugForLink = registration.slug || registration.event_name || registration.event;
            const eventHref = eventSlugForLink ? `/event/${eventSlugForLink}` : '';

            // Header-Style Event-Pass Card Template (my-events Navigation)
            slide.innerHTML = `
              <div class="event-pass-card${eventHref ? ' event-pass-card--clickable' : ''}" ${eventHref ? `role="link" tabindex="0"` : ''} data-event-href="${eventHref ? escapeAttr(eventHref) : ''}">
                <!-- Header mit Bild -->
                <div class="event-pass-header">
                  <img src="${escapeAttr(imageUrl)}" alt="${escapeAttr(registration.title)}" loading="lazy" />
                  <div class="event-pass-header-gradient"></div>

                  <!-- Countdown Badge (top-right) -->
                  ${countdown ? `
                  <div class="event-pass-countdown ${countdown.cssClass}">
                    <svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                    ${countdown.text}
                  </div>` : ''}

                  <!-- Status Badge (top-left) -->
                  <div class="event-pass-status ${statusBadge.cssClass}">
                    <svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="${statusBadge.icon}"/></svg>
                    ${statusBadge.text}
                  </div>
                </div>

                <!-- Content Section (unter dem Header) -->
                <div class="event-pass-content">
                  <h3 class="event-pass-title">${escapeHtml(registration.title) || __('Event folgt')}</h3>

                  <div class="event-pass-meta">
                    <div class="event-pass-meta-item">
                      ${getLocationIcon(registration)}
                      <span>${renderLocationInfo(registration, __('Ort wird bekanntgegeben'))}</span>
                    </div>
                    <div class="event-pass-meta-item">
                      ${icon('calendar')}
                      <span>${registration.start_date ? formatDate(registration.start_date) : ''}${registration.end_date ? ` – ${formatDate(registration.end_date)}` : ''}</span>
                    </div>
                    ${registration.ticket_name ? `
                    <div class="event-pass-ticket-badge">
                      <svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                        <path d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 002 2 2 2 0 010 4 2 2 0 00-2 2v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 00-2-2 2 2 0 010-4 2 2 0 002-2V7a2 2 0 00-2-2H5z"/>
                      </svg>
                      ${escapeHtml(registration.ticket_name)}
                    </div>` : ''}
                  </div>

                  <div class="event-pass-actions">
                    ${(registration.slug || registration.event_name || registration.event) ? `<a href="/event/${escapeAttr(registration.slug || registration.event_name || registration.event)}" class="event-pass-btn event-pass-btn-primary">
                      <svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                      ${__('Details')}
                    </a>` : ''}
                    ${(registration.registration_id && (registration.slug || registration.event_name || registration.event)) ? `<a href="/event/${escapeAttr(registration.slug || registration.event_name || registration.event)}/zugang" class="event-pass-btn event-pass-btn-secondary">
                      <svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"/>
                      </svg>
                      ${__('Ticket')}
                    </a>` : ''}
                    ${registration.pass_url ? `
                    <a href="${escapeAttr(registration.pass_url)}" class="event-pass-wallet-btn" target="_blank" rel="noopener noreferrer" title="${__('Zu Apple Wallet hinzufügen')}">
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                      </svg>
                      Wallet
                    </a>` : ''}
                  </div>
                </div>
              </div>
            `;
            myEventsWrapper.appendChild(slide);
          }

          // Issue #1009: delegate clicks on the tile body. Inner anchors
          // (Details / Ticket / Wallet buttons) keep their native click
          // behaviour because we explicitly return early when the click
          // originated on an <a>, <button>, or any element with its own
          // navigational data-attribute. Keyboard parity (Enter/Space on
          // the role="link" tile) is provided in the same handler.
          //
          // Memory: this whole loadMyEvents block re-runs on every refresh
          // (filter change, manual reload). The flag below ensures we
          // attach the click+keydown delegators on the wrapper only ONCE
          // for the lifetime of the page — without it, every refresh
          // would stack a new listener pair, doubling click handlers
          // until the tab is closed.
          if (!myEventsWrapper.dataset.citaTileDelegated) {
            myEventsWrapper.addEventListener('click', (ev) => {
              const card = ev.target.closest('.event-pass-card[data-event-href]');
              if (!card) return;
              if (ev.target.closest('a, button')) return;
              const href = card.getAttribute('data-event-href');
              if (href) window.location.href = href;
            });
            myEventsWrapper.addEventListener('keydown', (ev) => {
              if (ev.key !== 'Enter' && ev.key !== ' ') return;
              const card = ev.target.closest('.event-pass-card[data-event-href]');
              if (!card || ev.target !== card) return;
              ev.preventDefault();
              const href = card.getAttribute('data-event-href');
              if (href) window.location.href = href;
            });
            myEventsWrapper.dataset.citaTileDelegated = '1';
          }

          new Swiper('.my-events-carousel', {
            slidesPerView: 1.1,
            spaceBetween: 16,
            navigation: { nextEl: '.my-events-carousel .swiper-button-next', prevEl: '.my-events-carousel .swiper-button-prev' },
            pagination: {
              el: '.my-events-carousel .swiper-pagination',
              clickable: true,
              renderBullet: function (index, className) {
                return `<span class="event-pass-dot ${className}"></span>`;
              }
            },
            breakpoints: {
              480: { slidesPerView: 1.3, spaceBetween: 14 },
              640: { slidesPerView: 1.8, spaceBetween: 16 },
              1024: { slidesPerView: 2.5, spaceBetween: 20 }
            }
          });

          // Quick Stats aktualisieren (Backend-Stats nutzen, Fallback auf regs.length)
          const statTickets = document.getElementById('stat-tickets');
          const statEvents = document.getElementById('stat-events');
          if (statTickets) statTickets.textContent = dashboardData?.stats?.tickets_count ?? regs.length;
          if (statEvents) statEvents.textContent = dashboardData?.stats?.upcoming_events ?? regs.length;
        } else {
          // Stats auf 0 setzen wenn keine Events
          const statTickets = document.getElementById('stat-tickets');
          const statEvents = document.getElementById('stat-events');
          if (statTickets) statTickets.textContent = '0';
          if (statEvents) statEvents.textContent = '0';
          // Empty State anzeigen
          myEventsWrapper.innerHTML = '';
          const emptyState = document.getElementById('my-events-empty');
          const carousel = document.querySelector('.my-events-carousel');
          if (emptyState) emptyState.classList.remove('hidden');
          if (carousel) carousel.classList.add('hidden');
        }
      } catch (err) {
        if (dev) console.error('My Events failed', err);
        myEventsWrapper.innerHTML = `<div class="text-center text-secondary py-10">${__('Fehler beim Laden deiner Events.')}</div>`;
      }
    }

    // --- Watchlist (Karussell) ---
    const watchlistWrapper = document.getElementById('watchlist-swiper-wrapper');
    if (watchlistWrapper) {
      // Skeleton loading
      for (let i = 0; i < 2; i++) {
        const sk = document.createElement('div');
        sk.className = 'swiper-slide';
        sk.innerHTML = `<div class="skeleton-card"><div class="skeleton-line"></div><div class="skeleton-small-line"></div></div>`;
        watchlistWrapper.appendChild(sk);
      }

      try {
        const watchlist = dashboardData?.watchlist || [];
        watchlistWrapper.innerHTML = "";

        // Watchlist-Count aktualisieren
        const watchlistCountEl = document.getElementById('watchlist-count');
        if (watchlistCountEl) watchlistCountEl.textContent = watchlist.length;

        // Stat-Element aktualisieren (falls vorhanden)
        const statWatchlist = document.getElementById('stat-watchlist');
        if (statWatchlist) statWatchlist.textContent = watchlist.length;

        if (Array.isArray(watchlist) && watchlist.length) {
          for (const event of watchlist) {
            const slide = document.createElement('div');
            slide.className = 'swiper-slide';
            const dateBadge = formatDateBadge(event.start_date);
            const imageUrl = event.banner_image || event.image || event.default_image || '/assets/cita_events/images/event/cita_default_messe.png';

            // Compact Watchlist Card Template
            slide.innerHTML = `
              <div class="watchlist-card">
                <!-- Header mit Mini-Bild -->
                <div class="watchlist-header">
                  <img src="${escapeAttr(imageUrl)}" alt="${escapeAttr(event.title)}" loading="lazy" />
                  <div class="watchlist-header-gradient"></div>

                  <!-- Datum-Badge (top-left) -->
                  ${dateBadge.day ? `
                  <div class="watchlist-date">
                    <div class="watchlist-date-day">${dateBadge.day}</div>
                    <div class="watchlist-date-month">${dateBadge.month}</div>
                  </div>` : ''}

                  <!-- Heart-Button (top-right) -->
                  <button class="watchlist-heart active"
                          data-watch-event="${escapeAttr(event.name)}"
                          data-cita-action="CitaStart.toggleWatch" data-cita-arg="${escapeAttr(event.name)}" data-cita-arg-type="string" data-cita-prevent="true" data-cita-stop="true"
                          title="${__('Von Watchlist entfernen')}">
                    ${icon('heart')}
                  </button>
                </div>

                <!-- Content Section -->
                <div class="watchlist-content">
                  <h3 class="watchlist-title">${escapeHtml(event.title) || __('Event')}</h3>
                  <div class="watchlist-meta">
                    <div class="watchlist-location">
                      ${getLocationIcon(event)}
                      <span>${renderLocationInfo(event, __('Ort folgt'))}</span>
                    </div>
                    ${event.event_type ? `<span class="watchlist-type">${escapeHtml(event.event_type)}</span>` : ''}
                  </div>
                  <div class="watchlist-actions">
                    <a href="/event/${escapeAttr(event.slug)}" class="watchlist-btn">
                      ${icon('info')}
                      Details
                    </a>
                  </div>
                </div>
              </div>
            `;
            watchlistWrapper.appendChild(slide);
          }

          // Swiper initialisieren (mehr Karten sichtbar wegen kleinerer Größe)
          watchlistSwiper = new Swiper('.watchlist-carousel', {
            slidesPerView: 1.5,
            spaceBetween: 12,
            navigation: { nextEl: '.watchlist-carousel .swiper-button-next', prevEl: '.watchlist-carousel .swiper-button-prev' },
            pagination: {
              el: '.watchlist-carousel .swiper-pagination',
              clickable: true,
              renderBullet: function (index, className) {
                return `<span class="watchlist-dot ${className}"></span>`;
              }
            },
            breakpoints: {
              480: { slidesPerView: 2.2, spaceBetween: 12 },
              640: { slidesPerView: 3, spaceBetween: 14 },
              1024: { slidesPerView: 4, spaceBetween: 16 }
            }
          });
        } else {
          // Empty State anzeigen
          watchlistWrapper.innerHTML = '';
          const emptyState = document.getElementById('watchlist-empty');
          const carousel = document.querySelector('.watchlist-carousel');
          if (emptyState) emptyState.classList.remove('hidden');
          if (carousel) carousel.classList.add('hidden');
        }
      } catch (err) {
        if (dev) console.error('Watchlist failed', err);
        watchlistWrapper.innerHTML = `<div class="text-center text-secondary py-10">${__('Fehler beim Laden der Watchlist.')}</div>`;
      }
    }

    // --- Notification Widget (auf /start) ---
    const notificationsWidget = document.getElementById('notifications-widget');
    const notificationsContent = document.getElementById('notifications-widget-content');
    const notificationsCount = document.getElementById('notifications-widget-count');
    const announcementsContainer = document.getElementById('announcements-container');

    if (notificationsWidget && notificationsContent) {
      try {
        const { normalized } = await apiGet('cita_events.api.notifications.get_notification_summary', { limit: 5, location: 'Nur Start' });
        if (normalized.success) {
          const { notifications, alerts, announcements, unread_count } = normalized.data || {};
          const hasItems = (notifications && notifications.length > 0) || (alerts && alerts.length > 0);

          // Platform Announcements als Banner rendern (separate Section)
          if (announcementsContainer && announcements && announcements.length > 0) {
            let announcementHtml = '';
            for (const a of announcements) {
              // Skip if already dismissed
              const dismissKey = `announcement_dismissed_${a.code}`;
              if (a.is_dismissable && localStorage.getItem(dismissKey)) continue;

              // Dezente Farben (Platform-Style: slate-basiert mit Akzent-Border)
              const colorMap = {
                blue: { bg: 'surface-elevated', border: 'border-cyan-500/30', icon: 'text-cyan-400' },
                green: { bg: 'surface-elevated', border: 'border-emerald-500/30', icon: 'text-emerald-400' },
                purple: { bg: 'surface-elevated', border: 'border-violet-500/30', icon: 'text-violet-400' },
                amber: { bg: 'surface-elevated', border: 'border-amber-500/30', icon: 'text-amber-400' },
                red: { bg: 'surface-elevated', border: 'border-rose-500/30', icon: 'text-rose-400' },
                transparent: { bg: 'bg-transparent', border: 'border-default-theme', icon: 'text-secondary' },
              };

              // SVG Icons (konsistente Stroke-Icons, Lucide-Style)
              const iconSvgMap = {
                info: '<circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/>',
                warn: '<path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><path d="M12 9v4"/><path d="M12 17h.01"/>',
                megaphone: '<path d="M3 11l18-5v12L3 13v-2z"/><path d="M11.6 16.8a3 3 0 1 1-5.8-1.6"/>',
                sparkles: '<path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5L12 3z"/><path d="M5 19l.5 1.5L7 21l-1.5.5L5 23l-.5-1.5L3 21l1.5-.5L5 19z"/>',
                wrench: '<path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>',
                gift: '<path d="M20 12v10H4V12"/><path d="M2 7h20v5H2z"/><path d="M12 22V7"/><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/>',
                bell: '<path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>',
                star: '<path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>',
                checkmark: '<circle cx="12" cy="12" r="10"/><path d="M9 12l2 2 4-4"/>',
                calendar: '<rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><path d="M16 2v4M8 2v4M3 10h18"/>',
              };

              const colors = colorMap[a.accent_color] || colorMap.blue;
              const iconPath = iconSvgMap[a.icon] || iconSvgMap.info;

              // Slim Inline Banner mit Accordion - kompakt, bei Klick expandierbar
              // Typografie angepasst an Schnellzugriff/Dashboard Widgets
              const hasExpandableContent = a.message && a.message.length > 0;
              const escapedTitle = escapeAttr(a.title) || __('Ankündigung');
              // FE-008: Message ist HTML aus Quill-Editor - SECURITY NOTE:
              // Serverseitig sanitized via EventAnnouncement._sanitize_html_fields()
              // Siehe: cita_events/doctype/event_announcement/event_announcement.py:12-20
              const messageHtml = a.message || '';

              // Type-Badge Text (falls vorhanden)
              const typeLabel = a.announcement_type ? escapeAttr(a.announcement_type) : '';

              announcementHtml += `
                <div class="announcement-banner rounded-lg border-l-[3px] ${colors.bg} ${colors.border} backdrop-blur-sm overflow-hidden" data-code="${escapeAttr(a.code)}">
                  <!-- Header: Icon + Titel + Chevron + Type + Action + Dismiss -->
                  <div class="flex items-start gap-3 px-4 py-2.5 ${hasExpandableContent ? 'cursor-pointer hover:bg-white/5 transition' : ''}"
                       ${hasExpandableContent ? `data-cita-action="CitaStart.toggleAnnouncement" data-cita-arg="${escapeAttr(a.code)}" data-cita-arg-type="string"` : ''}>
                    <!-- Icon -->
                    <svg class="w-4 h-4 ${colors.icon} shrink-0 mt-0.5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 24 24">${iconPath}</svg>
                    <!-- Titel + Chevron inline -->
                    <div class="flex-1 min-w-0">
                      <span class="announcement-title font-medium text-body leading-snug">
                        ${escapedTitle}
                        ${hasExpandableContent ? `<svg class="announcement-chevron inline-block w-3.5 h-3.5 text-muted-theme ml-1.5 align-text-bottom transition-transform duration-200" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 24 24"><path d="M19 9l-7 7-7-7"/></svg>` : ''}
                      </span>
                    </div>
                    <!-- Type Badge -->
                    ${typeLabel ? `<span class="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${colors.bg} ${colors.icon} border ${colors.border} shrink-0">${typeLabel}</span>` : ''}
                    <!-- Action Link -->
                    ${a.action_url ? `<a href="${escapeAttr(a.action_url)}" class="text-xs font-medium ${colors.icon} hover:text-white whitespace-nowrap transition shrink-0" data-cita-stop="true">${escapeAttr(a.action_label) || __('Mehr')} →</a>` : ''}
                    <!-- Dismiss -->
                    ${a.is_dismissable ? `
                      <button class="text-muted-theme hover:text-secondary p-0.5 transition shrink-0" data-cita-stop="true" data-cita-action="CitaStart.dismissAnnouncement" data-cita-arg="${escapeAttr(a.code)}" data-cita-arg-type="string">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 24 24"><path d="M6 18L18 6M6 6l12 12"/></svg>
                      </button>
                    ` : ''}
                  </div>
                  <!-- Expandable Content (nur wenn Message vorhanden) -->
                  ${hasExpandableContent ? `
                    <div class="announcement-content hidden px-4 pb-3 pt-0">
                      <!-- XSS-SAFE: server-sanitized HTML via EventAnnouncement._sanitize_html_fields() -->
                      <div class="text-sm text-secondary leading-relaxed pl-7 announcement-message [&_.ql-editor]:p-0 [&_p]:m-0 [&_p+p]:mt-1">${messageHtml}</div>
                    </div>
                  ` : ''}
                </div>`;
            }
            if (announcementHtml) {
              announcementsContainer.innerHTML = announcementHtml;
              announcementsContainer.classList.remove('hidden');
            }
          }

          if (hasItems) {
            // Widget anzeigen
            notificationsWidget.classList.remove('hidden');

            // Count aktualisieren
            if (notificationsCount) {
              notificationsCount.textContent = unread_count || (notifications?.length || 0) + (alerts?.length || 0);
            }

            // Content aufbauen
            let contentHtml = '';

            // FE-008: System Alerts - SECURITY NOTE:
            // Alert messages are HTML from Quill editor, sanitized in notification_service.py:96
            if (alerts && alerts.length > 0) {
              for (const alert of alerts) {
                const severityClass = alert.severity === 'Kritisch'
                  ? 'bg-red-500/20 border-red-500/50 text-red-300'
                  : alert.severity === 'Warnung'
                    ? 'bg-amber-500/20 border-amber-500/50 text-amber-300'
                    : 'surface-card border-strong-theme text-secondary';
                const severityIcon = alert.severity === 'Kritisch'
                  ? '<path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><path d="M12 9v4"/><path d="M12 17h.01"/>'
                  : '<circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/>';

                contentHtml += `
                  <div class="flex items-start gap-2 p-2.5 rounded-lg border ${severityClass} cursor-pointer hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 transition"
                       role="button" tabindex="0"
                       aria-haspopup="dialog"
                       aria-label="${escapeAttr(alert.title) || __('Systemhinweis')} – ${__('Benachrichtigungen öffnen')}"
                       data-cita-action="dispatch-event"
                       data-cita-event="cita:open-notifications">
                    <svg class="w-4 h-4 shrink-0 mt-0.5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 24 24">
                      ${severityIcon}
                    </svg>
                    <div class="flex-1 min-w-0">
                      <div class="font-medium text-sm">${escapeHtml(alert.title) || __('Systemhinweis')}</div>
                      ${alert.message ? `<div class="text-xs opacity-80 mt-0.5 line-clamp-2 [&_.ql-editor]:p-0 [&_p]:m-0">${alert.message}</div>` : ''} ${''/* XSS-SAFE: server-sanitized HTML from Quill editor (notification_service.py:96) */}
                    </div>
                  </div>`;
              }
            }

            // FE-008: User Notifications - SECURITY NOTE:
            // Notification messages are HTML, sanitized in notification_service.py:96
            if (notifications && notifications.length > 0) {
              for (const notif of notifications) {
                const typeIcon = notif.notification_type === 'Warteliste'
                  ? '<circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>'
                  : notif.notification_type === 'Event'
                    ? '<rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4"/><path d="M8 2v4"/><path d="M3 10h18"/>'
                    : notif.notification_type === 'Ticket'
                      ? '<path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z"/><path d="M13 5v2"/><path d="M13 17v2"/><path d="M13 11v2"/>'
                      : '<path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>';
                const isImportant = notif.is_important || notif.priority === 'urgent';
                const isHighPriority = notif.priority === 'high';

                contentHtml += `
                  <div class="flex items-start gap-2 p-2.5 rounded-lg cursor-pointer hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 transition ${isImportant ? 'bg-cyan-500/10 border border-cyan-500/30' : isHighPriority ? 'bg-amber-500/10 border border-amber-500/30' : 'surface-card border border-default-theme'}"
                       role="button" tabindex="0"
                       aria-haspopup="dialog"
                       aria-label="${escapeAttr(notif.title) || __('Benachrichtigung')} – ${__('Benachrichtigungen öffnen')}"
                       data-cita-action="dispatch-event"
                       data-cita-event="cita:open-notifications">
                    <svg class="w-4 h-4 shrink-0 mt-0.5 ${isImportant ? 'text-cyan-400' : isHighPriority ? 'text-amber-400' : 'text-secondary'}" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 24 24">
                      ${typeIcon}
                    </svg>
                    <div class="flex-1 min-w-0">
                      <div class="font-medium text-sm ${isImportant ? 'text-cyan-200' : isHighPriority ? 'text-amber-200' : 'text-body'}">${escapeHtml(notif.title) || __('Benachrichtigung')}</div>
                      ${notif.message ? `<div class="text-xs ${isImportant ? 'text-cyan-300/70' : isHighPriority ? 'text-amber-300/70' : 'text-secondary'} mt-0.5 line-clamp-2 [&_.ql-editor]:p-0 [&_p]:m-0">${notif.message}</div>` : ''} ${''/* XSS-SAFE: server-sanitized HTML from Quill editor (notification_service.py:96) */}
                      ${notif.action_url ? `<a href="${escapeAttr(notif.action_url)}" class="inline-block mt-1 text-xs ${isImportant ? 'text-cyan-300' : isHighPriority ? 'text-amber-300' : 'text-cyan-400'} hover:underline" data-cita-stop="true">${escapeHtml(notif.action_label) || __('Anzeigen')} \u2192</a>` : ''}
                    </div>
                  </div>`;
              }
            }

            notificationsContent.innerHTML = contentHtml;
          }
        }
      } catch (err) {
        if (dev) console.warn('Notifications widget failed', err);
        // Widget bleibt versteckt bei Fehlern
      }
    }

    // Global dismiss function for announcements (im CitaStart Namespace)
    // Security: localStorage key validation to prevent abuse
    window.CitaStart.dismissAnnouncement = function(code) {
      // Validate code format (like navbar.js)
      if (!code || code.length > 100 || !/^[a-zA-Z0-9_-]+$/.test(code)) {
        if (dev) console.warn('Invalid announcement code:', code);
        return;
      }
      localStorage.setItem(`announcement_dismissed_${code}`, 'true');
      const banner = document.querySelector(`[data-code="${CSS.escape(code)}"]`);
      if (banner) banner.remove();
    };

    // Toggle function for expandable announcements
    window.CitaStart.toggleAnnouncement = function(code) {
      // Validate code format
      if (!code || code.length > 100 || !/^[a-zA-Z0-9_-]+$/.test(code)) {
        return;
      }
      const banner = document.querySelector(`[data-code="${CSS.escape(code)}"]`);
      if (!banner) return;

      const content = banner.querySelector('.announcement-content');
      const chevron = banner.querySelector('.announcement-chevron');
      const title = banner.querySelector('.announcement-title');

      if (!content) return;

      const isExpanded = !content.classList.contains('hidden');

      if (isExpanded) {
        // Collapse
        content.classList.add('hidden');
        chevron?.classList.remove('rotate-180');
        title?.classList.add('truncate');
      } else {
        // Expand
        content.classList.remove('hidden');
        chevron?.classList.add('rotate-180');
        title?.classList.remove('truncate');
      }
    };

    // --- Öffentliche Eventliste (Infinite Scroll + Filter) ---
    const listContainer = document.getElementById('event-list');
    const loadMore = document.getElementById('load-more');
    const searchInput = document.getElementById('search-input');
    const categorySelect = document.getElementById('category-select');
    const priceSelect = document.getElementById('price-select');

    if (listContainer && loadMore) {
      let page = 0;
      let loading = false;

      async function fetchEvents(reset = false) {
        if (loading) return;
        loading = true;
        if (reset) {
          listContainer.innerHTML = '';
          page = 0;
        }
        loadMore.textContent = __('Laden...');

        try {
          const params = {
            page,
            search: searchInput?.value || '',
            category: categorySelect?.value || '',
            price: priceSelect?.value || ''
          };
          const { normalized } = await apiGet('cita_events.api.event.event_public_api.public_event_list', params);
          const events = normalized.success && Array.isArray(normalized.data?.events) ? normalized.data.events : [];
          events.forEach(ev => {
            const card = document.createElement('div');
            card.className = 'glass-dark rounded-xl p-4 shadow flex flex-col relative';

            // Kapazitäts-Info für Listenansicht
            let capacityInfo = '';
            if (ev.ui_remaining_allowed && typeof ev.available_tickets === 'number') {
              if (ev.available_tickets <= 0) {
                capacityInfo = '<span class="inline-flex items-center gap-1 text-xs text-red-400 font-medium"><svg class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"/></svg>' + __('Ausverkauft') + '</span>';
              } else if (ev.available_tickets <= 10 || (ev.total_tickets > 0 && (ev.available_tickets / ev.total_tickets) < 0.2)) {
                capacityInfo = `<span class="inline-flex items-center gap-1 text-xs text-orange-400 font-medium">${icon('exclamation', { size: 12 })}${__('Nur noch {0} {1}!').replace('{0}', ev.available_tickets).replace('{1}', ev.available_tickets === 1 ? __('Platz') : __('Plätze'))}</span>`;
              }
            }

            card.innerHTML = `
              <div class="aspect-[4/3] bg-cover bg-center rounded-lg mb-3 relative" style="background-image:url('${safeCssUrl(ev.banner_image || ev.image || ev.default_image || '/assets/cita_events/images/event/cita_default_messe.png')}')">
                ${ev.ui_remaining_allowed && ev.available_tickets <= 0 ? '<div class="absolute inset-0 surface-card flex items-center justify-center rounded-lg"><span class="text-red-400 font-bold">' + __('Ausverkauft') + '</span></div>' : ''}
              </div>
              <h3 class="text-lg font-bold mb-1">${escapeHtml(ev.title)}</h3>
              <div class="text-sm text-secondary mb-2 flex flex-wrap items-center gap-2">
                <span>${renderLocationInfo(ev, '')}${renderLocationInfo(ev, '') ? ' – ' : ''}${formatDate(ev.start_date)}</span>
                ${capacityInfo}
              </div>
              <p class="text-sm text-secondary line-clamp-3 mb-3">${escapeHtml(ev.teaser || '')}</p>
              <a href="/event/${escapeAttr(ev.slug)}" class="mt-auto text-purple-400 hover:text-purple-300">${__('Event ansehen')} →</a>`;
            listContainer.appendChild(card);
          });

          page += 1;
          loadMore.textContent = '';

          if (events.length === 0 && page === 1) {
            listContainer.innerHTML = '<p class="text-center text-secondary">' + __('Keine Events gefunden.') + '</p>';
          }
        } catch (err) {
          if (dev) console.error('public_event_list failed', err);
          loadMore.textContent = __('Fehler');
        } finally {
          loading = false;
        }
      }

      if (searchInput)  searchInput.addEventListener('input', () => fetchEvents(true));
      if (categorySelect) categorySelect.addEventListener('change', () => fetchEvents(true));
      if (priceSelect)    priceSelect.addEventListener('change', () => fetchEvents(true));

      window.addEventListener('scroll', () => {
        if (!loading && loadMore.getBoundingClientRect().top < window.innerHeight) {
          fetchEvents();
        }
      });

      fetchEvents();
    }
  });
})();
